import { ComponentChildren } from "preact"

import { AwardIcon, StarIcon, StethoscopeIcon } from "@components/preact/design-system"
import { cardVariants, imageVariants, itemVariants, titleVariants } from "./profileCardVariants"
import clsx from "clsx"
import { pluralize } from "@lib/helpers/pluralize"
import ProviderFallbackImage from "./ProviderFallbackImage"
import { VariantProps } from "class-variance-authority"

type CardVariants = VariantProps<typeof cardVariants>

type ProviderProfileCardProps = CardVariants & {
  name: string
  profileImage: string
  title: string
  ratingScore: number
  ratingsCount: number
  specialty: string
  yearsOfExperience: number
  children?: ComponentChildren
}

export const ProviderProfileCard = ({
  name,
  profileImage,
  title,
  ratingScore,
  ratingsCount,
  specialty,
  yearsOfExperience,
  children,
  layout = "horizontal",
  variant = "default",
}: ProviderProfileCardProps) => {
  const isStandardVariant = variant === "standard"

  let fallbackImageSize = 120
  if (variant === "compact") {
    fallbackImageSize = 96
  }
  if (isStandardVariant) {
    fallbackImageSize = 100
  }

  return (
    <div className={cardVariants({ layout, variant })}>
      {profileImage ? (
        <img
          src={profileImage}
          alt={`${name} profile picture`}
          className={clsx("rounded-full object-cover", imageVariants({ variant }))}
        />
      ) : (
        <div className={clsx("rounded-full flex items-center justify-center", imageVariants({ variant }))}>
          <ProviderFallbackImage
            width={fallbackImageSize}
            height={fallbackImageSize}
          />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <div className={clsx("flex flex-col", {
          "gap-1": !isStandardVariant,
          "gap-2": isStandardVariant,
        })}>
          <h3 className={titleVariants({ variant })}>
            {name}, {title}
          </h3>
          <ul className={clsx("flex flex-col", {
            "gap-1": !isStandardVariant,
            "gap-2": isStandardVariant,
          })}>
            <li className={itemVariants({ variant })}>
              <span className="text-yellow-400">
                <StarIcon width={isStandardVariant ? 24 : 20} height={isStandardVariant ? 24 : 21} />
              </span>
              <p>
                {ratingScore && ratingsCount ? (
                  <span className="flex gap-1">
                    <span className="text-neutral-60-primary-text font-medium">{ratingScore}</span>
                    <span className="text-nowrap">({ratingsCount} {pluralize(ratingsCount, "review")})</span>
                  </span>
                ) : (
                  <span>No reviews yet</span>
                )}
              </p>
            </li>

            {specialty && (
              <li className={itemVariants({ variant })}>
                <span className="text-neutral-35">
                  <StethoscopeIcon width={isStandardVariant ? 24 : 20} height={isStandardVariant ? 24 : 20} />
                </span>
                <p>
                  {specialty}
                </p>
              </li>
            )}
            {!!yearsOfExperience && (
              <li className={itemVariants({ variant })}>
                <span className="text-neutral-35">
                  <AwardIcon width={isStandardVariant ? 24 : 20} height={isStandardVariant ? 24 : 20} />
                </span>
                <p>
                  {yearsOfExperience} years of experience
                </p>
              </li>
            )}
          </ul>
        </div>
        {children}
      </div>
    </div>
  )
}
